import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import AppIcon from '@/components/AppIcon';

interface UpdateComposerProps {
  onPublish: (update: {
    content: string;
    postType: string;
    department?: string;
    tags?: string[];
    attachmentUrls?: string[];
  }) => void;
  currentUserDepartment?: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const UpdateComposer: React.FC<UpdateComposerProps> = ({ onPublish, currentUserDepartment }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState<string>('');
  const [updateType, setUpdateType] = useState<string>('');
  const [milestone, setMilestone] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const updateTypeOptions: SelectOption[] = [
    { value: 'team-achievement', label: t('performance.teamHub.typeTeamAchievement') },
    { value: 'progress-milestone', label: t('performance.teamHub.typeProgressMilestone') },
    { value: 'goal-update', label: t('performance.teamHub.typeGoalUpdate') },
    { value: 'recognition-highlight', label: t('performance.teamHub.typeRecognitionHighlight') },
    { value: 'general-update', label: t('performance.teamHub.typeGeneralUpdate') },
    { value: 'announcement', label: t('performance.teamHub.typeAnnouncement') },
    { value: 'question', label: t('performance.teamHub.typeQuestion') },
    { value: 'milestone', label: t('performance.teamHub.typeMilestone') }
  ];

  const handlePublish = () => {
    if (!content.trim() || !updateType) return;

    onPublish({
      content: content.trim(),
      postType: updateType,
      department: currentUserDepartment,
    });

    // Reset form
    setContent('');
    setUpdateType('');
    setMilestone('');
    setIsExpanded(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-3 mb-3 w-full">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center text-primary-foreground">
          <AppIcon name="Send" size={20} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground">
            {t('performance.teamHub.shareTeamUpdate')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('performance.teamHub.shareTeamUpdateDescription')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Select
          options={updateTypeOptions}
          value={updateType}
          onChange={(val: string) => setUpdateType(val)}
          placeholder={t('performance.teamHub.updateTypePlaceholder')}
        />

        <textarea
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          rows={isExpanded ? 6 : 3}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onFocus={() => setIsExpanded(true)}
          placeholder={t('performance.teamHub.composerPlaceholder')}
        />

        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            {content.length}/1000 {t('performance.teamHub.characters')}
          </span>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setContent('');
                setUpdateType('');
                setMilestone('');
                setIsExpanded(false);
              }}
              disabled={!content && !updateType}
            >
              <AppIcon name="X" size={16} className="me-1" />
              {t('performance.teamHub.clear')}
            </Button>
            <Button
              variant="default"
              onClick={handlePublish}
              disabled={!content.trim() || !updateType}
            >
              <AppIcon name="Send" size={16} className="me-1" />
              {t('performance.teamHub.publishUpdate')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateComposer;
